import { Color } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import React, { useContext } from 'react';
import { View } from '@shared/components/View';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { MBSelectableDot } from '@shared/components/list';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextOverlineBold, MBTypographyStyles } from '@shared/components/typography';
import { useTagOptions } from '@shared/scenes/workflows/edit/hooks';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';

interface Props {
  component:
    | MBAPI.WorkflowTemplate_components_TagComponent
    | MBAPI.WorkflowTemplate_components_AuditTagComponent;
  overridePath?: string;
}

export function DefaultTagSelection({ component, overridePath }: Props) {
  const { options, defaultValue, onSetDefault, getOverrideValue, onSetOverride } =
    useTagOptions(component);
  const colors = useContext(ColorThemeContext);
  const value = !isNil(overridePath) ? getOverrideValue(overridePath) : defaultValue;
  const setValue = !isNil(overridePath)
    ? (val: string[]) => onSetOverride(overridePath, val)
    : onSetDefault;

  function newMultiselectDefaults(selectedValue: string): string[] {
    if (value?.includes(selectedValue)) {
      return value.filter(item => item !== selectedValue);
    }
    return [selectedValue].concat(value ?? []);
  }

  function newSingleSelectDefault(selectedValue: string): string[] {
    if (value?.includes(selectedValue)) {
      return [];
    }
    return [selectedValue];
  }

  const onPress = (newValue: string) => {
    const newDefaults = component.multiselect
      ? newMultiselectDefaults(newValue)
      : newSingleSelectDefault(newValue);
    setValue(newDefaults);
  };

  return (
    <View paddingBottom={2}>
      <View paddingHorizontal={3} paddingVertical={2}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('workflowTemplate.responseOptions')}
        </MBTextOverlineBold>
      </View>
      {options.map((option, index) => {
        if (!isEmpty(option.label)) {
          return (
            <React.Fragment key={index}>
              <SelectTagRow
                option={option}
                isMultiselect={!!component.multiselect}
                isSelected={value?.includes(option.value)}
                onPress={onPress}
              />
            </React.Fragment>
          );
        }
        return null;
      })}
    </View>
  );
}

interface TagSelectionRowProps {
  option: MBAPI.WorkflowTemplate_components_TagComponent_options;
  isMultiselect: boolean;
  isSelected: boolean;
  onPress: (value: string) => void;
}

function SelectTagRow({ option, isMultiselect, isSelected, onPress }: TagSelectionRowProps) {
  return (
    <MBTouchableOpacity onPress={() => onPress(option.value)}>
      <View row centerVertically paddingVertical={0.5} paddingHorizontal={3}>
        <View row wrap shrinkWrap flex={4} style={{ alignContent: 'flex-start' }}>
          <Tag
            key={option.value}
            color={option.backgroundColor as Color}
            textStyle={MBTypographyStyles.body2}
            label={option.label}
          />
        </View>
        <View flex style={{ alignItems: 'flex-end' }}>
          <MBSelectableDot selected={isSelected} radio={!isMultiselect} />
        </View>
      </View>
    </MBTouchableOpacity>
  );
}
